// src/hrms/alarms/alarmSound.ts
import type { AlarmSound } from "./alarmTypes";

/**
 * The in-page alarm tone, synthesised with Web Audio (no asset to load, no
 * <audio> element for the browser to refuse). Chrome and Safari keep an
 * AudioContext suspended until the page gets a click or key press, so the
 * first one anywhere on the page unlocks it; until then the alarm dialog says
 * the sound is blocked and its button unlocks it.
 */

type Ctor = typeof AudioContext;

let context: AudioContext | null = null;
let loop: ReturnType<typeof setInterval> | null = null;
let playing: AlarmSound | null = null;
const listeners = new Set<() => void>();

function audioCtor(): Ctor | null {
  if (typeof window === "undefined") return null;
  return (window.AudioContext || (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext) ?? null;
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeAlarmAudio(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True once the browser lets this page make sound. */
export function alarmAudioReady() {
  return context?.state === "running";
}

/** Call from inside a click or key handler. */
export function unlockAlarmAudio() {
  const Ctor = audioCtor();
  if (!Ctor) return;
  if (!context) {
    try {
      context = new Ctor();
      context.onstatechange = emit;
    } catch {
      return;
    }
  }
  if (context.state !== "running") {
    void context.resume().then(emit, () => undefined);
  }
}

let unlockInstalled = false;
/** Unlocks on the page's first click or key press, so a later alarm can ring. */
export function installAlarmAudioUnlock() {
  if (unlockInstalled || typeof window === "undefined") return;
  unlockInstalled = true;
  const unlock = () => {
    unlockAlarmAudio();
    if (alarmAudioReady()) {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    }
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

function tone(ctx: AudioContext, at: number, frequency: number, duration: number, type: OscillatorType, peak: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, at);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + duration + 0.02);
}

/** One bar of the tone; returns how long the bar lasts, in seconds. */
function playBar(ctx: AudioContext, sound: AlarmSound): number {
  const start = ctx.currentTime + 0.02;
  if (sound === "chime") {
    // A struck bell: fundamental plus two partials, long decay, two notes.
    [[1318.5, 0], [1046.5, 0.55]].forEach(([frequency, offset]) => {
      tone(ctx, start + offset, frequency, 1.4, "sine", 0.22);
      tone(ctx, start + offset, frequency * 2.01, 0.8, "sine", 0.06);
      tone(ctx, start + offset, frequency * 3.02, 0.4, "sine", 0.03);
    });
    return 2.2;
  }
  // Classic alarm clock: four quick beeps, then a breath.
  for (let beat = 0; beat < 4; beat += 1) tone(ctx, start + beat * 0.2, 880, 0.12, "square", 0.12);
  return 1.4;
}

/** Loops the tone until stopAlarmSound(). Silently does nothing while audio is still locked. */
export function startAlarmSound(sound: AlarmSound) {
  const voice: AlarmSound = sound === "chime" ? "chime" : "classic";
  if (playing === voice && loop) return;
  stopAlarmSound();
  if (!context || context.state !== "running") return;
  const ctx = context;
  playing = voice;
  const seconds = playBar(ctx, voice);
  loop = setInterval(() => {
    if (ctx.state === "running") playBar(ctx, voice);
  }, seconds * 1000);
}

export function stopAlarmSound() {
  if (loop) clearInterval(loop);
  loop = null;
  playing = null;
}

/** One bar, for the settings form's "Test sound" button (a click, so it can unlock too). */
export function previewAlarmSound(sound: AlarmSound) {
  unlockAlarmAudio();
  if (!context) return;
  const ctx = context;
  const play = () => playBar(ctx, sound === "chime" ? "chime" : "classic");
  if (ctx.state === "running") play();
  else void ctx.resume().then(play, () => undefined);
}

export function isAlarmSoundPlaying() {
  return playing !== null;
}
